import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useWhiteboardContext } from '@src/contexts';
import { useExitRoomStore, usePeersStore, useRoomActionsStore } from '@src/stores';

import { EUserType } from '@src/types';
import { isWebView } from '@src/helpers';

type TRoomType = 'webinar' | 'meet';

const useExitRoomModal = (roomType: TRoomType) => {
  const { onDismiss, isOpenModal, setExitRoomState } = useExitRoomStore(
    useShallow((state) => ({
      onDismiss: state.onDismiss,
      setExitRoomState: state.setExitRoomState,
      isOpenModal: state.isOpenModal,
    }))
  );

  const { peers } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
    }))
  );

  const {
    exitRoomSettings: { onExitClick },
  } = useRoomActionsStore();

  const { handleCloseWhiteboard } = useWhiteboardContext();

  const numberOfHostsAndCoHosts = useMemo(() => {
    const hostRoles = new Set([EUserType.HOST, EUserType.CO_HOST]);

    const filteredPeers = Array.from(peers.values()).filter((peer) => {
      const userType = peer?.peer_info?.user_type;
      return userType && hostRoles.has(userType);
    });

    return filteredPeers.length;
  }, [peers]);

  const closeModal = () => {
    onDismiss();
    setExitRoomState({ isOpenModal: false });
  };

  const handleExitClick = (endForAll?: boolean) => {
    setTimeout(
      () => {
        endForAll ? onExitClick(endForAll) : onExitClick();
      },
      !isWebView() ? 0 : 100
    );
  };

  const isWebinarAndLastHostOrCoHost = roomType === 'webinar' && numberOfHostsAndCoHosts === 1;

  const buttons = isWebinarAndLastHostOrCoHost
    ? [
        {
          text: 'Yes',
          onClick: () => {
            closeModal();
            handleExitClick(true);
          },
        },
        {
          text: 'Cancel',
          onClick: closeModal,
        },
      ]
    : [
        {
          text: 'Leave the call',
          onClick: () => {
            handleCloseWhiteboard(true);
            closeModal();
            handleExitClick();
          },
        },
        {
          text: 'End for everyone',
          onClick: () => {
            closeModal();
            handleExitClick(true);
          },
        },
      ];

  const title = isWebinarAndLastHostOrCoHost
    ? 'Are you sure you want to close webinar for everyone?'
    : 'Would you like to leave the call or end it for everyone?';

  return { isOpenModal, closeModal, title, buttons };
};

export default useExitRoomModal;
